/**
 * Generic auto-wrap — brief §6.
 *
 * The key insight: per-variant wrap reduces to "find the placement whose
 * inverse maps the exited point back inside the tile polygon". Each variant
 * only has to supply a correct transform set; wrap correctness then follows
 * and is uniformly testable (round-trip property).
 */
import type { TileTransform, WrapResult } from '@/patterns/types';
import { apply, invert, isIdentityTransform, transformToMatrix } from './affine';

const EPS = 1e-9;

/** Ray-cast point-in-polygon with boundary tolerance. */
export function pointInPolygon(polygon: [number, number][], x: number, y: number, eps = 1e-7): boolean {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi + EPS) {
      inside = !inside;
    }
  }
  if (inside) return true;
  return distanceToPolygonEdges(polygon, x, y) <= eps;
}

/** Min distance from a point to the polygon's edges. */
export function distanceToPolygonEdges(polygon: [number, number][], x: number, y: number): number {
  let best = Infinity;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    const dx = xi - xj;
    const dy = yi - yj;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq === 0 ? 0 : ((x - xj) * dx + (y - yj) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const px = xj + t * dx - x;
    const py = yj + t * dy - y;
    best = Math.min(best, Math.hypot(px, py));
  }
  return best;
}

/**
 * Map a point that has exited the tile back to its equivalent tile-space
 * position. `transforms` must include enough placements to cover the point
 * (ring 2 is plenty for adjacent-edge exits).
 *
 * Returns the identity placement when the point is already inside.
 * Falls back to the placement that gets closest when no exact hit exists
 * (numerical edge grazing) — never throws during a live stroke.
 */
export function wrapPointGeneric(
  polygon: [number, number][],
  transforms: TileTransform[],
  x: number,
  y: number,
  eps = 1e-7,
): WrapResult {
  if (pointInPolygon(polygon, x, y, eps)) {
    return { x, y, transform: { tx: 0, ty: 0, rotation: 0, flipX: false, flipY: false } };
  }

  let bestDist = Infinity;
  let best: WrapResult | null = null;

  for (const t of transforms) {
    if (isIdentityTransform(t)) continue;
    const inv = invert(transformToMatrix(t));
    const [px, py] = apply(inv, [x, y]);
    if (pointInPolygon(polygon, px, py, eps)) {
      return { x: px, y: py, transform: t };
    }
    const d = distanceToPolygonEdges(polygon, px, py);
    if (d < bestDist) {
      bestDist = d;
      best = { x: px, y: py, transform: t };
    }
  }

  // Numerical fallback — closest placement; callers may also re-call with a
  // larger transform range if bestDist is large.
  if (best) return best;
  return { x, y, transform: { tx: 0, ty: 0, rotation: 0, flipX: false, flipY: false } };
}

/**
 * Offset a convex polygon outward by a constant distance `epsilon` (an exact
 * Minkowski-sum-with-a-disc corner, via intersecting each edge's offset line
 * with its neighbours — not a per-vertex push along the vertex normal, which
 * would drift for unevenly-spaced vertices and round corners incorrectly).
 *
 * Bug 5 (sketch tile seams): TileCanvas.vue renders each tile placement by
 * clipping to this polygon and blitting the layer raster into it. The raster
 * itself is already seamless — useDrawing.ts duplicates every near-edge mark
 * through the ring-1 symmetry transforms so the *content* lines up exactly
 * at a shared edge. What isn't seamless is Canvas2D's clip antialiasing:
 * two tiles whose clip paths exactly abut can each rasterise their shared
 * edge at less than full coverage on that boundary pixel, so the two halves
 * don't sum back to opaque and a hairline of the background shows through.
 * Outsetting the clip polygon by a sub-pixel amount makes neighbouring tiles
 * overlap slightly instead of exactly abutting — since the content in that
 * overlap is identical on both sides (by construction, via the ring-1
 * duplication), the extra overlap is invisible and the seam closes.
 *
 * Convex-only: offset edge-lines of a concave polygon can cross before
 * reaching the neighbour they're meant to meet. Every fundamental-domain
 * tile shape this app generates (fundamental.ts) is convex, so that's the
 * only case this needs to handle.
 */
export function outsetPolygon(polygon: [number, number][], epsilon: number): [number, number][] {
  const n = polygon.length;
  if (n < 3 || epsilon === 0) return polygon;
  const cx = polygon.reduce((s, p) => s + p[0], 0) / n;
  const cy = polygon.reduce((s, p) => s + p[1], 0) / n;

  // Each edge's offset line: a point on it (the edge start, pushed out along
  // the edge's outward normal by epsilon) plus the edge's direction vector.
  const lines: { px: number; py: number; dx: number; dy: number }[] = [];
  for (let i = 0; i < n; i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[(i + 1) % n]!;
    const dx = xj - xi;
    const dy = yj - yi;
    let nx = dy;
    let ny = -dx;
    const mx = (xi + xj) / 2;
    const my = (yi + yj) / 2;
    if (nx * (mx - cx) + ny * (my - cy) < 0) {
      nx = -nx;
      ny = -ny;
    }
    const len = Math.hypot(nx, ny) || 1;
    nx /= len;
    ny /= len;
    lines.push({ px: xi + nx * epsilon, py: yi + ny * epsilon, dx, dy });
  }

  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = lines[(i - 1 + n) % n]!;
    const b = lines[i]!;
    // Intersect offset-line(i-1) with offset-line(i) — their intersection is
    // the new position of the vertex shared by those two edges.
    const denom = a.dx * b.dy - a.dy * b.dx;
    if (Math.abs(denom) < 1e-12) {
      out.push(polygon[i]!); // degenerate (parallel neighbours) — leave as-is
      continue;
    }
    const t = ((b.px - a.px) * b.dy - (b.py - a.py) * b.dx) / denom;
    out.push([a.px + t * a.dx, a.py + t * a.dy]);
  }
  return out;
}

/** Outward edge-normal sample points just outside the polygon — test + tooling helper. */
export function pointsJustOutside(polygon: [number, number][], offset = 1e-4, perEdge = 7): [number, number][] {
  const out: [number, number][] = [];
  const n = polygon.length;
  const cx = polygon.reduce((s, p) => s + p[0], 0) / n;
  const cy = polygon.reduce((s, p) => s + p[1], 0) / n;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    for (let k = 1; k <= perEdge; k++) {
      const t = k / (perEdge + 1);
      const ex = xj + (xi - xj) * t;
      const ey = yj + (yi - yj) * t;
      // normal to the edge, oriented away from centroid
      let nx = yi - yj;
      let ny = -(xi - xj);
      const dot = nx * (ex - cx) + ny * (ey - cy);
      if (dot < 0) {
        nx = -nx;
        ny = -ny;
      }
      const len = Math.hypot(nx, ny);
      out.push([ex + (nx / len) * offset, ey + (ny / len) * offset]);
    }
  }
  return out;
}
